import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";

import type { SncrNumeracaoBloco } from "../helpers/basicExtensions";
import { listSncrNumeracaoBlocos } from "../services/sncrNumeracaoBasicService";
import type { SncrNotificacaoTipo } from "../types";
import { useSncrNotificacao } from "./useSncrNotificacao";

type Options = {
  enabled?: boolean;
};

export function useSncrNumeracoesPanel({ enabled = true }: Options = {}) {
  const { user } = useSessionContext();
  const notificacao = useSncrNotificacao();

  const [blocos, setBlocos] = useState<SncrNumeracaoBloco[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState<SncrNotificacaoTipo | "ALL">(
    "ALL",
  );

  const practitionerReference = user?.username
    ? `Practitioner/${oids.cpf}-${user.username}`
    : undefined;

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoadingList(true);
    try {
      const list = await listSncrNumeracaoBlocos({
        practitionerReference,
      });
      setBlocos(list);
    } catch (error) {
      console.error("[SNCR] Falha ao listar blocos", error);
      toast.error("Não foi possível carregar as numerações SNCR.");
    } finally {
      setLoadingList(false);
    }
  }, [enabled, practitionerReference]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, reload]);

  const blocosFiltrados = useMemo(() => {
    if (tipoFiltro === "ALL") return blocos;
    return blocos.filter((b) => b.tipoReceita === tipoFiltro);
  }, [blocos, tipoFiltro]);

  async function solicitarEAtualizar() {
    await notificacao.solicitar();
    await reload();
  }

  return {
    ...notificacao,
    blocos: blocosFiltrados,
    allBlocos: blocos,
    loadingList,
    reload,
    tipoFiltro,
    setTipoFiltro,
    solicitarEAtualizar,
  };
}
